import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["currencyItem", "priceElement"]
  static values = {
    currentCurrency: String,
    exchangeRate: Number
  }

  connect() {
    window.addEventListener("currency:convert", this.updateAllPrices.bind(this));

    const savedCurrency = localStorage.getItem('selectedCurrency')
    const savedExchangeRate = localStorage.getItem('exchangeRate')
    const savedCountryCode = localStorage.getItem('selectedCountryCode')
    const lastFetchTime = localStorage.getItem('currencyDataFetchTime')

    if (savedCurrency && savedExchangeRate) {
      this.currentCurrencyValue = savedCurrency
      this.exchangeRateValue = parseFloat(savedExchangeRate)

      this.updateButtonDisplay(savedCurrency, savedCountryCode || 'US')
    }

    const now = new Date().getTime()
    const oneDayMs = 24 * 60 * 60 * 1000

    if (!lastFetchTime || (now - parseInt(lastFetchTime)) > oneDayMs) {
      this.fetchCurrencyData()
    } else {
      // use cache data if present
      const cachedData = localStorage.getItem('currencyData')
      if (cachedData) {
        try {
          const data = JSON.parse(cachedData)
          this.processData(data)
        } catch (e) {
          console.error('Error parsing cached currency data:', e)
          this.fetchCurrencyData()
        }
      } else {
        this.fetchCurrencyData()
      }
    }
  }

  fetchCurrencyData() {
    fetch("https://widgets.trip101.com/rules", { // todo:: move this to env variable
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        if (data && data.location_dets) {
          // Cache data
          localStorage.setItem('currencyData', JSON.stringify(data))
          localStorage.setItem('currencyDataFetchTime', new Date().getTime())

          // Set default currency if not already set
          if (!localStorage.getItem('selectedCurrency') || !localStorage.getItem('exchangeRate') || !localStorage.getItem('selectedCountryCode')) {
            localStorage.setItem('selectedCurrency', data.location_dets.local_location.currency)
            localStorage.setItem('exchangeRate', data.location_dets.local_location.exchange_rate)
            localStorage.setItem('selectedCountryCode', data.location_dets.local_location.country_code)
          }

          this.processData(data)

          // Update button display for the first time
          const localLocation = data.location_dets.local_location
          this.updateButtonDisplay(localLocation.currency, localLocation.country_code)

        } else {
          console.error("Invalid data format from currency API")
        }
      })
      .catch(error => {
        console.error("Error fetching currency data:", error)
        // if cache exists, update prices
        if (this.currentCurrencyValue && this.exchangeRateValue) {
          this.updateAllPrices()
        }
      })
  }

  processData(data) {
    const localLocation = data.location_dets.local_location

    if (!this.currentCurrencyValue || !this.exchangeRateValue) {
      this.currentCurrencyValue = localLocation.currency
      this.exchangeRateValue = localLocation.exchange_rate
    }

    const allLocations = [localLocation, ...data.location_dets.other_locations]
    const uniqueCurrenciesMap = new Map()
    allLocations.forEach(location => {
      if (!uniqueCurrenciesMap.has(location.currency) || location === localLocation) {
        uniqueCurrenciesMap.set(location.currency, location)
      }
    })
    this.availableCurrencies = Array.from(uniqueCurrenciesMap.values())

    const currencyDropdown = document.getElementById("currency-dropdown")
    if (currencyDropdown) {
      currencyDropdown.innerHTML = ""
      this.availableCurrencies.forEach(location => {
        const isActive = location.currency === this.currentCurrencyValue
        this.addCurrencyItem(currencyDropdown, location, isActive)
      })
    }

    this.updateAllPrices()
  }

  addCurrencyItem(container, location, isActive) {
    const item = document.createElement("a")
    item.href = "#"
    item.className = "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
    if (isActive) {
      item.classList.add("bg-gray-100")
    }

    // Get flag emoji based on country code
    const flagEmoji = this.getFlagEmoji(location.country_code)

    item.innerHTML = `
      <span class="inline-block w-5 h-5 mr-2 -mb-1">${flagEmoji}</span> ${location.currency}
    `

    item.dataset.currency = location.currency
    item.dataset.exchangeRate = location.exchange_rate
    item.dataset.countryCode = location.country_code
    item.dataset.action = "click->currency#changeCurrency"
    item.dataset.currencyTarget = "currencyItem"

    container.appendChild(item)
  }

  getFlagEmoji(countryCode) {
    return `<img src="https://flagcdn.com/${countryCode.toLowerCase()}.svg" alt="${countryCode}" class="w-5 h-5 object-contain" />`
  }

  changeCurrency(event) {
    event.preventDefault()

    const newCurrency = event.currentTarget.dataset.currency
    const newExchangeRate = parseFloat(event.currentTarget.dataset.exchangeRate)
    const countryCode = event.currentTarget.dataset.countryCode || 'US'

    this.currentCurrencyValue = newCurrency
    this.exchangeRateValue = newExchangeRate

    this.updateButtonDisplay(newCurrency, countryCode)

    this.updateAllPrices()

    localStorage.setItem('selectedCurrency', newCurrency)
    localStorage.setItem('exchangeRate', newExchangeRate)
    localStorage.setItem('selectedCountryCode', countryCode)

    const dropdown = document.getElementById('currency-dropdown')
    if (dropdown) {
      dropdown.classList.add('hidden')
    }

    const customEvent = new CustomEvent('currencyChanged', {
      detail: {
        currency: newCurrency,
        exchangeRate: newExchangeRate,
        countryCode: countryCode
      }
    });
    window.dispatchEvent(customEvent);
  }

  updateAllPrices() {
    // Find all price elements with data-original-price attribute
    const priceElements = document.querySelectorAll('[data-original-price]')

    priceElements.forEach(element => {
      const originalPrice = parseFloat(element.dataset.originalPrice)
      if (!isNaN(originalPrice)) {
        const convertedPrice = originalPrice * this.exchangeRateValue
        element.textContent = this.formatPrice(convertedPrice, this.currentCurrencyValue)
      }
    })
  }

  formatPrice(price, currency) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })

    return formatter.format(price)
  }

  updateButtonDisplay(currency, countryCode) {
    const currencyButton = document.querySelector("[data-dropdown-id='currency-dropdown']")
    if (currencyButton) {
      const flagSpan = currencyButton.querySelector(".currency-flag")
      const textSpan = currencyButton.querySelector(".currency-code")

      if (flagSpan && textSpan) {
        flagSpan.innerHTML = this.getFlagEmoji(countryCode)
        textSpan.textContent = currency
      }
    }
  }
}
